import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { eventAPI } from '../services/eventApi';
import { AppEvent } from '../types/event';
import EventCard from '../components/events/EventCard';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import { format } from 'date-fns';
import { CalendarIcon, ListBulletIcon } from '@heroicons/react/24/outline';

type ViewMode = 'list' | 'calendar';
type EventTab = 'upcoming' | 'past';

export default function EventsCalendar() {
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState<EventTab>('upcoming');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [recordingsOnly, setRecordingsOnly] = useState(false);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const EVENTS_PER_PAGE = 10;

  const getEventStartDate = (event: AppEvent): Date => new Date(event.startAt);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const loaded = await eventAPI.fetchEvents();
        const sortedEvents = [...loaded].sort(
          (a, b) => getEventStartDate(a).getTime() - getEventStartDate(b).getTime()
        );
        setEvents(sortedEvents);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load events'));
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Filter events by date range
  const getUpcomingEvents = () => {
    const now = new Date();
    return events.filter((event) => {
      const startDate = getEventStartDate(event);
      return startDate >= now;
    });
  };

  const getPastEvents = () => {
    const now = new Date();
    return events
      .filter((event) => {
        const startDate = getEventStartDate(event);
        return startDate < now;
      })
      .sort((a, b) => getEventStartDate(b).getTime() - getEventStartDate(a).getTime());
  };

  const pastEventYears = useMemo(() => {
    const years = new Set<number>();
    getPastEvents().forEach((event) => {
      years.add(getEventStartDate(event).getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [events]);

  const filteredPastEvents = useMemo(() => {
    const past = getPastEvents().filter((event) => !recordingsOnly || Boolean(event.recordingUrl));
    if (selectedYear === 'all') return past;
    return past.filter(
      (event) => getEventStartDate(event).getFullYear() === Number(selectedYear)
    );
  }, [events, selectedYear, recordingsOnly]);

  const paginatedUpcoming = useMemo(() => {
    return getUpcomingEvents().slice(0, upcomingPage * EVENTS_PER_PAGE);
  }, [events, upcomingPage]);

  const paginatedPast = useMemo(() => {
    return filteredPastEvents.slice(0, pastPage * EVENTS_PER_PAGE);
  }, [filteredPastEvents, pastPage]);

  const renderCalendar = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (number | null)[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    const getEventsForDate = (day: number) => {
      const date = new Date(year, month, day);
      return events.filter((event) => {
        const startDate = getEventStartDate(event);
        return (
          startDate.getFullYear() === date.getFullYear() &&
          startDate.getMonth() === date.getMonth() &&
          startDate.getDate() === date.getDate()
        );
      });
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {format(selectedDate, 'MMMM yyyy')}
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setSelectedDate(new Date(year, month - 1, 1));
              }}
              className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => {
                setSelectedDate(new Date());
              }}
              className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Today
            </button>
            <button
              onClick={() => {
                setSelectedDate(new Date(year, month + 1, 1));
              }}
              className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center text-sm font-semibold text-gray-700 py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            if (day === null) {
              return <div key={index} className="min-h-[100px]" />;
            }

            const dayEvents = getEventsForDate(day);
            const now = new Date();
            const isToday =
              day === now.getDate() &&
              month === now.getMonth() &&
              year === now.getFullYear();
            const isPast = new Date(year, month, day) < new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const maxVisible = 2;

            return (
              <div
                key={index}
                className={`min-h-[100px] border rounded-lg p-1.5 transition-colors ${
                  isToday
                    ? 'bg-primary/5 border-primary ring-1 ring-primary/20'
                    : dayEvents.length > 0
                      ? 'border-gray-200 hover:border-primary/30 hover:bg-gray-50'
                      : 'border-gray-100 bg-gray-50/30'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-sm font-semibold inline-flex items-center justify-center w-7 h-7 rounded-full ${
                      isToday
                        ? 'bg-primary text-white'
                        : isPast
                          ? 'text-gray-400'
                          : 'text-gray-900'
                    }`}
                  >
                    {day}
                  </span>
                  {dayEvents.length > 0 && (
                    <span className="text-[10px] font-medium text-gray-400">
                      {dayEvents.length}
                    </span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, maxVisible).map((evt) => {
                    const evtStart = getEventStartDate(evt);
                    return (
                      <Link
                        key={evt.id}
                        to={`/events/${evt.slug}`}
                        className={`block text-[10px] leading-tight px-1.5 py-0.5 rounded truncate transition-colors ${
                          isPast
                            ? 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            : 'bg-primary/10 text-primary hover:bg-primary/20'
                        }`}
                        title={evt.title}
                      >
                        {!evt.allDay && (
                          <span className="font-semibold">{format(evtStart, 'h:mma').toLowerCase()} </span>
                        )}
                        {evt.title}
                      </Link>
                    );
                  })}
                  {dayEvents.length > maxVisible && (
                    <span className="block text-[10px] text-gray-400 font-medium px-1.5">
                      +{dayEvents.length - maxVisible} more
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Events Calendar
            </h1>
            <p className="text-gray-600">
              Stay updated with upcoming webinars, conferences, and training sessions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-2 rounded-md border transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
              aria-label="Calendar view"
            >
              <CalendarIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md border transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
              aria-label="List view"
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <LoadingSkeleton key={i} lines={3} className="h-32" />
          ))}
        </div>
      )}

      {error && (
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      )}

      {!loading && !error && viewMode === 'calendar' && renderCalendar()}

      {!loading && !error && viewMode === 'list' && (
        <div className="space-y-6">
          {/* Upcoming / Past Tabs */}
          <div className="flex items-center gap-6 border-b border-gray-200">
            <button
              onClick={() => { setActiveTab('upcoming'); setSelectedYear('all'); setUpcomingPage(1); }}
              className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${
                activeTab === 'upcoming'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Upcoming ({getUpcomingEvents().length})
            </button>
            <button
              onClick={() => { setActiveTab('past'); setPastPage(1); }}
              className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${
                activeTab === 'past'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Past Events ({getPastEvents().length})
            </button>
          </div>

          {/* Upcoming Events */}
          {activeTab === 'upcoming' && (
            <>
              {getUpcomingEvents().length > 0 ? (
                <>
                <div className="space-y-4">
                  {paginatedUpcoming.map((event) => (
                    <EventCard key={event.id} event={event} layout="list" />
                  ))}
                </div>
                {paginatedUpcoming.length < getUpcomingEvents().length && (
                  <div className="flex justify-center pt-4">
                    <button
                      onClick={() => setUpcomingPage((p) => p + 1)}
                      className="px-6 py-2.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors"
                    >
                      Show More ({getUpcomingEvents().length - paginatedUpcoming.length} remaining)
                    </button>
                  </div>
                )}
                </>
              ) : (
                <EmptyState
                  title="No upcoming events"
                  description="Check back later for upcoming events and webinars."
                />
              )}
            </>
          )}

          {/* Past Events */}
          {activeTab === 'past' && (
            <>
              {getPastEvents().length > 0 ? (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      aria-pressed={recordingsOnly}
                      onClick={() => { setRecordingsOnly((on) => !on); setPastPage(1); }}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        recordingsOnly
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      With recordings
                    </button>
                    {pastEventYears.length > 1 && (
                      <>
                        <span className="text-sm font-medium text-gray-500">Year:</span>
                        <button
                          type="button"
                          onClick={() => { setSelectedYear('all'); setPastPage(1); }}
                          className={`px-3 py-1 text-sm rounded-full transition-colors ${
                            selectedYear === 'all'
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          All
                        </button>
                        {pastEventYears.map((year) => (
                          <button
                            key={year}
                            type="button"
                            onClick={() => { setSelectedYear(String(year)); setPastPage(1); }}
                            className={`px-3 py-1 text-sm rounded-full transition-colors ${
                              selectedYear === String(year)
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </>
                    )}
                  </div>

                  <div className="space-y-4">
                    {paginatedPast.map((event) => (
                      <EventCard key={event.id} event={event} layout="list" past />
                    ))}
                  </div>

                  {paginatedPast.length < filteredPastEvents.length && (
                    <div className="flex justify-center pt-4">
                      <button
                        onClick={() => setPastPage((p) => p + 1)}
                        className="px-6 py-2.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors"
                      >
                        Show More ({filteredPastEvents.length - paginatedPast.length} remaining)
                      </button>
                    </div>
                  )}

                  {filteredPastEvents.length === 0 && (
                    <EmptyState
                      title={recordingsOnly ? 'No recordings for this selection' : `No events in ${selectedYear}`}
                      description={recordingsOnly ? 'Try another year, or turn off With recordings.' : 'Try selecting a different year.'}
                    />
                  )}
                </>
              ) : (
                <EmptyState
                  title="No past events"
                  description="Past events will appear here once they have occurred."
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

